import type { ExtensionApi } from "@shopify/web-pixels-extension";

export interface KeyValueStore {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

export interface CookieOptions {
  maxAgeSeconds?: number;
  path?: string;
  domain?: string;
  sameSite?: "strict" | "lax" | "none";
  secure?: boolean;
}

export interface CookieStore {
  get(name: string): Promise<string | null>;
  set(name: string, value: string, options?: CookieOptions): Promise<void>;
}

export interface Environment {
  localStorage: KeyValueStore;
  cookies: CookieStore;
  randomUUID(): string;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const browserGlobals = globalThis as unknown as {
  localStorage?: {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
  };
  document?: { cookie: string };
};

export function generateUuid(): string {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } })
    .crypto;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === "x" ? r : (r & 3) | 8;
    return v.toString(16);
  });
}

export class BrowserEnvironment implements Environment {
  localStorage: KeyValueStore = {
    async getItem(key) {
      return browserGlobals.localStorage?.getItem(key) ?? null;
    },
    async setItem(key, value) {
      browserGlobals.localStorage?.setItem(key, value);
    },
    async removeItem(key) {
      browserGlobals.localStorage?.removeItem(key);
    },
  };

  cookies: CookieStore = {
    async get(name) {
      const doc = browserGlobals.document;
      if (!doc) {
        return null;
      }
      const prefix = `${name}=`;
      const match = doc.cookie.split("; ").find((c) => c.startsWith(prefix));
      return match ? decodeURIComponent(match.slice(prefix.length)) : null;
    },
    async set(name, value, options) {
      const doc = browserGlobals.document;
      if (!doc) {
        return;
      }
      const parts = [
        `${name}=${encodeURIComponent(value)}`,
        `path=${options?.path ?? "/"}`,
      ];
      if (options?.maxAgeSeconds != null) {
        parts.push(`max-age=${options.maxAgeSeconds}`);
      }
      if (options?.domain) {
        parts.push(`domain=${options.domain}`);
      }
      if (options?.sameSite) {
        parts.push(`samesite=${options.sameSite}`);
      }
      if (options?.secure) {
        parts.push("secure");
      }
      doc.cookie = parts.join("; ");
    },
  };

  randomUUID(): string {
    return generateUuid();
  }
}

export class ShopifySandboxEnvironment implements Environment {
  readonly localStorage: KeyValueStore;
  readonly cookies: CookieStore;

  constructor(api: ExtensionApi) {
    this.localStorage = {
      getItem: (key) => api.browser.localStorage.getItem(key),
      setItem: (key, value) => api.browser.localStorage.setItem(key, value),
      removeItem: (key) => api.browser.localStorage.removeItem(key),
    };
    this.cookies = {
      get: (name) => api.browser.cookie.get(name),
      set: async (name, value) => {
        await api.browser.cookie.set(name, value);
      },
    };
  }

  randomUUID(): string {
    return generateUuid();
  }
}
